import React from "react";
import { Schedule, PlanningClassData } from "../../../../../types/planningTypes";
import { timeToMinutes, minutesToTime } from "../../utils/timeUtils";
import { getSubjectCodeFromkey } from "utils/schoolUtils";

interface CellContentProps {
  cls: PlanningClassData;
  day: { label: string; value: string };
  slot: { start: string; end: string };
  schedules: Schedule[];
  handleCellClick: (data: {
    type: "add" | "edit";
    schedule?: Schedule;
    classId: string;
    dayOfTheWeek: string;
    startTime: string;
    endTime: string;
  }) => void;
}

const DEFAULT_SESSION_MINUTES = 60;

const CellContent: React.FC<CellContentProps> = ({
  cls,
  day,
  slot,
  schedules,
  handleCellClick,
}) => {
  const slotStartMin = timeToMinutes(slot.start);
  const slotEndMin = timeToMinutes(slot.end);

  const getSubjectInfo = (sched: Schedule) => ({
    subjectCode: getSubjectCodeFromkey(
      sched.classSubject?.subject?.code || "N/A"
    ),
    colorCode: sched.classSubject?.subject?.colorCode,
  });

  // All schedules intersecting this slot (08-10, 10-12, ...)
  const slotSchedules = schedules.filter(
    (sched) =>
      sched.classSubjectId &&
      cls.classSubject.some((cs) => cs.id === sched.classSubjectId) &&
      sched.dayOfTheWeek === day.value &&
      sched.startTime < slot.end &&
      sched.endTime > slot.start
  );

  // sort by start time for display
  const orderedSlotSchedules = [...slotSchedules].sort(
    (a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime)
  );

  const handleAddClick = () => {
    const defaultEnd = minutesToTime(slotStartMin + DEFAULT_SESSION_MINUTES);

    handleCellClick({
      type: "add",
      classId: cls.id,
      dayOfTheWeek: day.value,
      startTime: slot.start,
      endTime: defaultEnd > slot.end ? slot.end : defaultEnd,
    });
  };

  return (
    <div className="time-slot-container stacked" onClick={handleAddClick}>
      {orderedSlotSchedules.map((sched) => {
        const { subjectCode, colorCode } = getSubjectInfo(sched);

        const displayStart = sched.startTime.slice(0, 5);
        const displayEnd = sched.endTime.slice(0, 5);
        const teacherUser = sched?.classSubject?.teacher?.user;
        const teacherName = teacherUser
          ? `${teacherUser.firstName} ${teacherUser.lastName}`
          : "—";

        return (
          <div
            key={sched.id}
            className="schedule-pill"
            style={{ background: colorCode || "#FFF" }}
            onClick={(e) => {
              e.stopPropagation(); 
              handleCellClick({
                type: "edit",
                schedule: sched,
                classId: cls.id,
                dayOfTheWeek: day.value,
                startTime: sched.startTime,
                endTime: sched.endTime,
              });
            }}
          >
            <div className="schedule-pill-subject">{subjectCode}</div>
            <div className="schedule-pill-time">
              {displayStart} – {displayEnd}
            </div>
            <div className="schedule-pill-teacher">
              {teacherUser?.photo && (
                <img
                  src={teacherUser.photo}
                  alt={teacherName}
                  className="teacher-avatar"
                />
              )}
              <span className="teacher-name">{teacherName}</span>
            </div>
          </div>
        );
      })}

      {slotSchedules.length === 0 && (
        <span className="add-symbol">+</span>
      )}
    </div>
  );
};

export default CellContent;
